/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// paste-cluster.js — copy/paste of a MULTI-SELECTION as one rigidly-arranged
// cluster of FRESH parts. Copy captures each member's kind/ref/params (with
// run-volatile state stripped — a paste is brand new) and the world point it is
// anchored at; paste translates the whole arrangement by a single integer-pitch
// shift and asks, per member, "does this land legally?".
//
// Across, the desk is one lattice (every hole at a whole pitch); down it is
// not, so the shift's dy is borrowed from the strip under the lead member
// (`clusterShift`). Either way the translation is rigid, so a hole-anchored
// member lands squarely on another hole or misses — never half a hole off —
// and "is every pin over an open hole?" stays a crisp yes/no per member.
//
// Pure and DOM-free: the controller feeds it a plain `{boards, components,
// wires}` and gets back a per-member seat + legality. Board-part collision
// stays occupancy's authority (canPlacePart); brick collision stays the
// document's (a passed canPlaceBrick).
//
// There is deliberately NO member-vs-member collision check. A captured cluster
// comes from a document where every pin already owns a distinct hole and no two
// bricks overlap, and a rigid integer translation preserves both (it's
// injective on holes and overlap-preserving on rects). So the ONLY way a member
// is illegal is by colliding with the EXISTING document or landing off a board
// — never with a sibling. (A source lead that floats over bare desk can't
// change that: to contend with a sibling's pin it would have had to sit on that
// sibling's occupied hole at the source, which no valid document allows.)

import { BRICK_KINDS, partDef } from "../catalog/index.js";
import { dropRunLatches } from "../catalog/run-latches.js";
import { formatAddress, parseAddress } from "./breadboard.js";
import { seatClassOf } from "./footprints.js";
import {
  addressAtWorld,
  canPlacePart,
  holeAtWorld,
  worldOfAddress,
} from "./occupancy.js";

/**
 * The rigid shift a paste ghost takes for a pointer travel of `raw`.
 *
 * Across, the pitch IS the lattice, so the travel rounds to whole pitches.
 * DOWN there is no lattice (CLAUDE.md → "Domain reference"): a kit's
 * pin-board rows sit on .01 while a loose Tiny 170's sit on .51, so a whole-
 * pitch dy could never land a cluster copied off one on the other — every
 * member was half a pitch off its holes and the ghost red everywhere. So the
 * first board-seated member BORROWS the lattice of whatever strip is under it
 * (the hole nearest its raw landing point), exactly as the cluster drag does;
 * the rest follow rigidly, and one on a strip at some other offset lands
 * between holes and is honestly red. A cluster of bricks alone, or a lead
 * member over nothing, keeps the rounded shift.
 *
 * @param {Array} boards
 * @param {Array<{ref:string, params?:object, anchorWorld:{x:number,y:number}}>} members
 * @param {{dx:number, dy:number}} raw
 * @returns {{dx:number, dy:number}}
 */
export function clusterShift(boards, members, raw) {
  const rounded = { dx: Math.round(raw.dx), dy: Math.round(raw.dy) };
  const lead = (members ?? []).find(
    (m) => memberForm(m.ref, m.params) !== "brick",
  );
  if (!lead) return rounded;
  const hit = holeAtWorld(
    boards,
    lead.anchorWorld.x + rounded.dx,
    lead.anchorWorld.y + raw.dy,
  );
  if (!hit) return rounded;
  return {
    dx: Math.round((hit.x - lead.anchorWorld.x) * 100) / 100,
    dy: Math.round((hit.y - lead.anchorWorld.y) * 100) / 100,
  };
}

/**
 * A member's placement FORM — drives its ghost drawing and how its seat
 * resolves. Null for an unknown ref.
 */
export function memberForm(ref, params) {
  const def = partDef(ref);
  if (!def) return null;
  if (def.package) return "chip"; // DIP straddling the trench
  if (BRICK_KINDS.has(def.kind)) return "brick"; // desk-level
  if (def.rotatable && params?.rot === 90) return "turned"; // two free ends
  if (Array.isArray(params?.legs) && def.freeLegs?.(params)) return "legs"; // a bent TO-92
  return "discrete"; // linear footprint along one grid row
}

/**
 * The world (pitch) point a component's placement is anchored to — its anchor
 * hole for a board part (chip: row-e pin 1; discrete/turned: pin 1's hole), or
 * its stored top-left for a desk brick. Null when it can't be resolved.
 */
export function memberAnchorWorld(boards, comp) {
  if (comp.board == null) {
    return Number.isFinite(comp.x) && Number.isFinite(comp.y)
      ? { x: comp.x, y: comp.y }
      : null;
  }
  return worldOfAddress(boards, formatAddress(comp.board, comp.anchor));
}

/**
 * Capture a fresh cluster from a set of source components. Each member keeps
 * only its kind/ref/params (deep-copied, with run-volatile 12 V damage stripped
 * — a paste is a brand-new part, never a reference to its source) and the world
 * point it is anchored at, so the arrangement can be re-stamped rigidly. A DIP
 * also keeps the ROW its pin 1 is in (`chipRow`), which is its seat's width
 * (footprints.js `dipRows`): rigid means it lands the width it was.
 * Members whose anchor can't resolve are skipped; null when nothing usable
 * remains.
 *
 * @param {Array} boards
 * @param {Array} comps - full component objects (from doc.getComponent)
 * @returns {{members: Array, center: {x:number,y:number}}|null}
 */
export function captureCluster(boards, comps) {
  const members = [];
  for (const comp of comps ?? []) {
    if (!comp || !partDef(comp.ref)) continue;
    const anchorWorld = memberAnchorWorld(boards, comp);
    if (!anchorWorld) continue;
    const params = comp.params ? JSON.parse(JSON.stringify(comp.params)) : {};
    dropRunLatches(params); // run-volatile — a fresh part is never pre-damaged
    const member = { kind: comp.kind, ref: comp.ref, params, anchorWorld };
    if (memberForm(comp.ref, params) === "chip") {
      member.chipRow = seatClassOf(comp.anchor);
    }
    members.push(member);
  }
  if (members.length === 0) return null;
  // The grab reference: the arrangement's bounding-box centre, so the cluster
  // tracks the cursor from its middle. Fractional is fine — the shift rounds.
  const xs = members.map((m) => m.anchorWorld.x);
  const ys = members.map((m) => m.anchorWorld.y);
  const center = {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
  return { members, center };
}

/**
 * Resolve every member of a cluster shifted by `shift` (integer pitch): its
 * target seat and whether it lands legally. A board part is legal when it lands
 * with EVERY pin over a free hole (occupancy's canPlacePart); a brick when
 * `canPlaceBrick`. A member over bare desk (no hole under its anchor) is
 * illegal, and so is a DIP whose pin 1 lands in any row but the one it was
 * captured in — a shift of whole rows would otherwise re-seat a 600-mil chip
 * at the other width, which no rigid move does. See the module note on why no
 * member-vs-member check is needed.
 *
 * @param {{boards:Array, components:Array, wires?:Array}} doc - the live document
 * @param {Array} members - from captureCluster
 * @param {{dx:number, dy:number}} shift - integer pitch translation
 * @param {(ref:string, x:number, y:number)=>boolean} canPlaceBrick
 * @returns {Array<{kind,ref,params,anchorWorld,form,seat,legal}>}
 *   seat: board part → {board, anchor}; brick → {x, y}; null when unresolved.
 */
export function resolveCluster(doc, members, shift, canPlaceBrick) {
  const boards = doc.boards ?? [];
  return (members ?? []).map((m) => {
    const form = memberForm(m.ref, m.params);
    const ax = m.anchorWorld.x + shift.dx;
    const ay = m.anchorWorld.y + shift.dy;
    if (form === "brick") {
      // Where `addBrick` will actually put it (whole units), so the spot
      // checked is the spot committed — the shift's dy is often fractional.
      const seat = { x: Math.round(ax), y: Math.round(ay) };
      return { ...m, form, seat, legal: canPlaceBrick(m.ref, seat.x, seat.y) };
    }
    // A board part: the shifted anchor either lands squarely on a hole or over
    // nothing. No hole → nowhere to seat → illegal.
    const parsed = parseAddress(addressAtWorld(boards, ax, ay));
    if (!parsed) return { ...m, form, seat: null, legal: false };
    const seat = { board: parsed.boardId, anchor: parsed.hole };
    if (m.chipRow != null && seatClassOf(seat.anchor) !== m.chipRow) {
      return { ...m, form, seat, legal: false };
    }
    const legal = canPlacePart(doc, {
      ref: m.ref,
      board: seat.board,
      anchor: seat.anchor,
      params: m.params,
    });
    return { ...m, form, seat, legal };
  });
}
